import React from "react"
import tw, { styled, css } from "twin.macro"
import { Answer, QuestionDetailFragment } from "../../graphql/generated/graphql"
import { Routes } from "../../routes"
import Link from "next/link"
import { ReactComponent as PolygonSVG } from "../../assets/icons/polygon.svg"
import { ReactComponent as PlaySvg } from "../../assets/play.svg"
import Image from "next/image"
import { IconSize, Share } from "../share"
import { DomainPath } from "../../utils/context"
import { EmojiReactions } from "../emojiReactions"
import Datetime from "../datetime"
import { getBlockchainDetails } from "../nftBar/providerConfig"
import { MediaTranscodeFilename, MediaTranscodeMp4, MediaTranscodesLoader } from "../../utils/loader"
import { youTubeBaseUrl } from "../../utils"
import { PlatformIcon } from "../questionCard"

const PlayButton = styled(PlaySvg)(() => [
  css`
    width: 52px;
    height: 52px;
    opacity: 0.8;
    path {
      fill: white;
    }
  `,
  tw`rounded-lg m-auto right-0 bottom-0 top-0 left-0 absolute`,
])

interface AnswerCardProps {
  question: QuestionDetailFragment
  isReactable: boolean
  refetchQuestion?: () => Promise<Record<string, any>>
  useEmojiSmall: boolean
  isPlayable: boolean
  referringProfileUsername: string
}

export const AnswerCard = ({
  question,
  isReactable,
  refetchQuestion,
  useEmojiSmall,
  isPlayable,
  referringProfileUsername,
}: AnswerCardProps) => {
  const { Answer, id, supportPayments, platform, SourceComment } = question
  const { createdAt, nftNumber, youTubeId, shareRendition, igShareLink } = Answer as Answer
  const openOpenSeaLink = () => {
    const blockchainDetails = getBlockchainDetails()
    window.open(`${blockchainDetails.opensea}${blockchainDetails.contract}/${nftNumber}`, "_blank")
  }

  // Get the array of mintedPayments from supportPayments
  const mintedPayments = supportPayments && supportPayments.filter((payment) => payment.isMinted === true)

  // if one has been minted, show the opensea icon
  const minted = (
    <>
      {mintedPayments && mintedPayments.length > 0 && (
        <div
          data-testid={`minted`}
          tw="flex items-center space-x-1 hover:(cursor-pointer underline)"
          onClick={openOpenSeaLink}
        >
          <span>Minted</span>
          <PolygonSVG data-testid={`open-sea-logo-${id}`} />
        </div>
      )}
    </>
  )

  const posterImage = MediaTranscodesLoader({ src: MediaTranscodeFilename(Answer), width: 2000 })
  const shareUrl = `${DomainPath()}/q/${id}/`
  const youTubeUrl = !!youTubeId ? `${youTubeBaseUrl}/${youTubeId}` : undefined

  return (
    <div
      data-testid="answered"
      tw="w-full tablet:(max-w-[500px]) space-y-2 flex flex-col border-base-2 border-2 rounded-lg p-2 hover:border-purple"
    >
      <div
        data-testid="video-created-at"
        tw="text-base-3 text-xs flex no-underline hover:(!no-underline) items-center justify-between"
      >
        <Datetime datetime={createdAt} />
        {minted}
        {platform && <PlatformIcon {...{ mediaUrl: SourceComment?.mediaUrl, platform }} />}
      </div>
      {Answer && isPlayable ? (
        <video
          data-testid="video-player"
          controls
          tw="w-full rounded"
          src={MediaTranscodeMp4(Answer)}
          preload="metadata"
          poster={posterImage}
        >
          <source src={MediaTranscodeMp4(Answer)} />
        </video>
      ) : (
        <Link href={{ pathname: Routes.ShowQuestion, query: { id, profile: referringProfileUsername } }} passHref>
          <a data-testid="answer-card" tw="relative cursor-pointer">
            <Image
              loader={MediaTranscodesLoader}
              src={MediaTranscodeFilename(Answer)}
              sizes="(max-width: 768px) 80vw, 30vw"
              width={500}
              height={500}
              tw="rounded-lg"
            />
            <PlayButton />
          </a>
        </Link>
      )}
      <div tw="flex justify-between items-center">
        <EmojiReactions {...{ question, isReactable, refetchQuestion, useEmojiSmall }} />
        <Share
          {...{
            shareUrl,
            igShareLink,
            youTubeUrl,
            size: IconSize.W28,
            shareId: id,
            useButton: true,
            hasRenditions: !!shareRendition,
          }}
        />
      </div>
    </div>
  )
}
